import { useLayoutEffect, useRef } from "react";

// Controls that make up the filter section under the heading.
const FILTER_CONTROL_SELECTOR = "button, select, .selection-drop";
// Parts of the heading line that stay where they are.
const HEADING_SELECTOR =
  ".dashboard-detail-back-btn, .dashboard-detail-title, .dashboard-detail-count, .dashboard-detail-heading-label-cluster, .ages-milestone-title, .ages-milestone-count";
// The black rule between a report's two columns, whatever the page calls it.
const COLUMN_DIVIDER_SELECTOR = '[class*="column-divider"]';

// Per report page: whether the reader has touched its filters, and the shift
// each filter line was held at (by its place on the heading line).
const heldPages = new WeakMap();

/**
 * Desktop only: slides the filter section under the heading (the search box
 * and the filter bar under it) sideways so it is centred on the report. On a
 * report split into two columns the centre is the column rule, which moves
 * with the first column's width; on a one-column report it is the middle of
 * the heading line. Each line of the section — the box, the bar — is centred
 * on its own, so a bar narrower than the box sits centred under it.
 *
 * Re-measured whenever the list or the window changes. A line too long to
 * centre without running past an edge of the heading line leaves the whole
 * section left justified, unshifted; on a phone the section keeps its place
 * at the left too.
 *
 * Once the reader touches the filters (a click, a key, typing in the box) the
 * section holds where it is: picking a line, a month or a sort, or searching,
 * reshapes the list under it, and re-centring on every change made the box
 * and the buttons jump about. Only a window resize centres it again. The
 * hold is kept per page, so a box that remounts while the list reloads comes
 * back at the same place.
 *
 * A `translate` rather than padding or margin, so no page's own sizing of the
 * box or the bar is disturbed.
 *
 * @param {React.RefObject<HTMLElement>} anchorRef — the heading line, or any
 *   element inside it (the search box)
 * @param {boolean} [enabled=true]
 * @param {boolean} [frozen=false] — keep the current shift instead of
 *   re-centring (a page whose list is filtered down keeps its filters where
 *   the full report put them)
 * @param {React.RefObject<HTMLElement>} [centreOnRef] — a one-column report
 *   narrower than its heading line: centre on this element (the list) instead
 *   of the middle of the heading line
 */
export default function useCentreFilterSection(anchorRef, enabled = true, frozen = false, centreOnRef) {
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    // The milestone pages name their heading line their own way; only one
    // that says what to centre on (Decade Ages this Year) is slid.
    const header = anchor?.closest(
      centreOnRef ? ".dashboard-detail-header-left, .ages-milestone-header-left" : ".dashboard-detail-header-left"
    );
    const page = anchor?.closest(".dashboard-detail-page");
    if (!enabled || !header || !page) return undefined;

    const offsets = new Map();
    let frame = 0;
    if (!heldPages.has(page)) heldPages.set(page, { held: false, shifts: [] });
    const hold = heldPages.get(page);

    const filterSections = () =>
      Array.from(header.children).filter(
        (child) =>
          !child.matches(HEADING_SELECTOR) &&
          !child.querySelector(HEADING_SELECTOR) &&
          (child.matches(".dashboard-detail-search") ||
            child.querySelector(`.dashboard-detail-search, ${FILTER_CONTROL_SELECTOR}`))
      );

    const findDivider = () =>
      Array.from(page.querySelectorAll(COLUMN_DIVIDER_SELECTOR)).find((el) => {
        if (header.contains(el)) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

    const shift = (section, next) => {
      offsets.set(section, next);
      section.style.translate = next ? `${next}px 0` : "";
    };

    const reset = (sections) => {
      sections.forEach((section) => shift(section, 0));
      header.classList.remove("dashboard-detail-header-left--centred");
    };

    // A section's own extent: the search input and every control, read with
    // its current shift taken back out. The input rather than its wrapper —
    // on some pages the wrapper runs the full width of the line.
    const extent = (section) => {
      const offset = offsets.get(section) || 0;
      let left = Infinity;
      let right = -Infinity;
      const parts = section.matches(".dashboard-detail-search")
        ? [section.querySelector(".dashboard-detail-search-input")]
        : section.querySelectorAll(`.dashboard-detail-search-input, ${FILTER_CONTROL_SELECTOR}`);
      parts.forEach((part) => {
        if (!part) return;
        // An open dropdown's options are not part of the section.
        if (part.closest('[role="listbox"], [role="menu"]')) return;
        // The search box's clear button sits inside the input's span.
        if (part.closest(".dashboard-detail-search") && !part.matches(".dashboard-detail-search-input")) {
          return;
        }
        const rect = part.getBoundingClientRect();
        if (rect.width === 0) return;
        left = Math.min(left, rect.left - offset);
        right = Math.max(right, rect.right - offset);
      });
      return Number.isFinite(left) ? { left, right } : null;
    };

    const centre = () => {
      frame = 0;
      const sections = filterSections();
      if (!window.matchMedia("(min-width: 769px)").matches) {
        reset(sections);
        return;
      }
      if (hold.held || frozenRef.current) {
        // Sections this effect has not placed yet (a remount) take the held shift.
        sections.forEach((section, index) => {
          if (!offsets.has(section)) shift(section, hold.shifts[index] || 0);
        });
        header.classList.toggle(
          "dashboard-detail-header-left--centred",
          sections.some((section) => offsets.get(section))
        );
        return;
      }
      if (!sections.length || !window.matchMedia("(min-width: 769px)").matches) {
        reset(sections);
        return;
      }

      const headerRect = header.getBoundingClientRect();
      const divider = findDivider();
      let target = headerRect.left + headerRect.width / 2;
      const centreOnRect = centreOnRef?.current?.getBoundingClientRect();
      if (centreOnRect && centreOnRect.width > 0) {
        target = centreOnRect.left + centreOnRect.width / 2;
      }
      if (divider) {
        const dividerRect = divider.getBoundingClientRect();
        target = dividerRect.left + dividerRect.width / 2;
      }

      const boxes = sections.map(extent);
      const shiftTo = (box, centre) => {
        if (!box) return { next: 0, fits: true };
        const wanted = centre - (box.left + box.right) / 2;
        const min = Math.min(0, headerRect.left - box.left);
        const max = Math.max(0, headerRect.right - box.right);
        const next = Math.round(Math.min(max, Math.max(min, wanted)));
        return { next, fits: Math.abs(next - wanted) < 1 };
      };
      const wantedShifts = boxes.map((box) => shiftTo(box, target));
      const tooLong = wantedShifts.some(({ fits }) => !fits);

      // A line too long to centre (it would run past an edge of the heading
      // line) stays at the left margin as laid out, rather than pushed against
      // the far edge out of line with the list. The shorter lines — the search
      // box — are centred over that widest line instead.
      let fallbackShifts = null;
      if (tooLong) {
        const widest = boxes.reduce(
          (best, box) => (box && (!best || box.right - box.left > best.right - best.left) ? box : best),
          null
        );
        const centre = widest
          ? (Math.max(widest.left, headerRect.left) + Math.min(widest.right, headerRect.right)) / 2
          : target;
        fallbackShifts = boxes.map((box) => (box === widest ? 0 : shiftTo(box, centre).next));
      }

      let moved = false;
      sections.forEach((section, index) => {
        const next = tooLong ? fallbackShifts[index] : wantedShifts[index].next;
        shift(section, next);
        if (next) moved = true;
      });
      hold.shifts = sections.map((section) => offsets.get(section) || 0);
      header.classList.toggle("dashboard-detail-header-left--centred", moved);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(centre);
    };

    const holdPlace = () => {
      hold.held = true;
    };
    const onResize = () => {
      hold.held = false;
      schedule();
    };
    header.addEventListener("pointerdown", holdPlace, true);
    header.addEventListener("keydown", holdPlace, true);
    header.addEventListener("input", holdPlace, true);

    centre();

    // The rule moves as the first column grows or shrinks, and the columns come
    // and go as the list loads, splits or is searched.
    const ro = new ResizeObserver(schedule);
    const observeLayout = () => {
      ro.disconnect();
      ro.observe(header);
      header
        .querySelectorAll(`.dashboard-detail-search-input, ${FILTER_CONTROL_SELECTOR}`)
        .forEach((control) => ro.observe(control));
      if (centreOnRef?.current) ro.observe(centreOnRef.current);
      const divider = findDivider();
      if (divider?.parentElement) {
        ro.observe(divider.parentElement);
        Array.from(divider.parentElement.children).forEach((child) => ro.observe(child));
      }
    };
    const mo = new MutationObserver(() => {
      observeLayout();
      schedule();
    });
    observeLayout();
    mo.observe(page, { childList: true, subtree: true });
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", onResize);
      header.removeEventListener("pointerdown", holdPlace, true);
      header.removeEventListener("keydown", holdPlace, true);
      header.removeEventListener("input", holdPlace, true);
      reset(filterSections());
    };
  }, [anchorRef, enabled, centreOnRef]);
}
